import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
} from '@mui/material';
import { Add, Edit, Delete, Close, Print, ExpandLess, ExpandMore, UnfoldLess, UnfoldMore, Lock } from '@mui/icons-material';
import StandardPagination from './Pagination/StandardPagination';
import SavingOverlay from './SavingOverlay';
import ConfirmModal from './modals/ConfirmModal';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import {
  patientNotesService,
  mapPatientNoteError,
  PatientNote,
  PatientNoteAuthor,
  PATIENT_NOTE_MAX_LENGTH,
  NoteVisibility,
  NotePriority,
  NOTE_PRIORITIES,
  NOTE_VISIBILITIES,
} from '../services/patientNotesService';
import { colors, typography, inputs } from '../theme/designSystem';
import ClearFiltersButton from './ClearFiltersButton';
import { filterFieldSx, searchFieldSx, dateFieldSx, filterActionButtonSx } from '../theme/filterStyles';

/**
 * Aba Anotações do cadastro do paciente (RN-534): anotações GERAIS, visíveis a todo perfil
 * com acesso ao cadastro (Secretário, Administrador e Profissional) — sem conteúdo clínico
 * nem particular. O autor altera/exclui só dentro da janela de edição (7 dias); depois vira
 * somente leitura para todos. Escrita dupla no Bubble (RN-302) no servidor.
 */
interface Props {
  patientId: string;
}

const DEFAULT_PAGE_SIZE = 10;
const MIN_SEARCH_LENGTH = 3;

const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const timeOf = (iso: string): string => iso.slice(11, 16);

/** Texto do card: colapsado em UMA linha; "Ler mais" aparece só quando o texto não coube
    (medido no elemento — inclui quebra de linha e estouro de largura). */
const NoteText: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const ref = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (expanded) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setOverflows(el.scrollHeight - el.clientHeight > 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  return (
    <>
      <Typography
        ref={ref}
        sx={{
          color: colors.text,
          lineHeight: 1.6,
          fontSize: '0.875rem',
          overflowWrap: 'anywhere',
          whiteSpace: 'pre-wrap',
          ...(expanded ? {} : {
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {text}
      </Typography>
      {(overflows || expanded) && (
        <Typography
          component="button"
          type="button"
          onClick={() => setExpanded(v => !v)}
          sx={{ background: 'none', border: 'none', padding: 0, mt: 0.5, font: 'inherit', fontSize: '0.8rem', color: colors.primary, cursor: 'pointer', textDecoration: 'underline' }}
        >
          {expanded ? 'Ler menos' : 'Ler mais'}
        </Typography>
      )}
    </>
  );
};

/** Aviso único da aba e do modal: quem vê e o que NÃO escrever aqui. */
const PatientNotesTab: React.FC<Props> = ({ patientId }) => {
  const [items, setItems] = useState<PatientNote[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [authors, setAuthors] = useState<PatientNoteAuthor[]>([]);
  const [editWindowDays, setEditWindowDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<PatientNote | null>(null);
  const [visibility, setVisibility] = useState<NoteVisibility>('author');
  const [priority, setPriority] = useState<NotePriority>('medium');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  // Acordeão por linha + "abrir todas" (padrão das abas de registros em série)
  const [expanded, setExpanded] = useState<string[]>([]);
  const isExpanded = (id: string) => expanded.includes(id);
  const toggleExpanded = (id: string) => setExpanded(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const [toDelete, setToDelete] = useState<PatientNote | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { toast, showToast, hideToast } = useToast();
  const loadSeqRef = useRef(0);

  // Busca só a partir de 3 letras (abaixo disso a lista fica sem o filtro de texto)
  const effectiveSearch = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : '';
  const invalidRange = !!from && !!to && to < from;

  const load = useCallback(async () => {
    if (invalidRange) {
      setItems([]);
      setTotalCount(0);
      setError('A data final não pode ser anterior à data inicial.');
      setLoading(false);
      return;
    }
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const result = await patientNotesService.getByPatient(patientId, {
        search: effectiveSearch || undefined,
        createdBy: createdBy || undefined,
        from: from || undefined,
        to: to || undefined,
        pageNumber: page,
        pageSize,
      });
      if (seq !== loadSeqRef.current) return;
      setItems(result.items);
      setTotalCount(result.totalCount);
      setAuthors(result.authors);
      setEditWindowDays(result.editWindowDays ?? 7);
    } catch {
      if (seq !== loadSeqRef.current) return;
      setError('Não foi possível carregar as anotações do paciente.');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [patientId, effectiveSearch, createdBy, from, to, page, pageSize, invalidRange]);

  useEffect(() => {
    load();
  }, [load]);

  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };
  const hasFilters = !!search || !!createdBy || !!from || !!to;
  const clearFilters = () => {
    setSearch('');
    setCreatedBy('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setText('');
    setVisibility('author');
    setPriority('medium');
    setSaveError('');
    setIsModalOpen(true);
  };
  const openEdit = (note: PatientNote) => {
    setEditing(note);
    setText(note.note);
    setVisibility(note.visibility);
    setPriority(note.priority);
    setSaveError('');
    setIsModalOpen(true);
  };
  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditing(null);
    setText('');
    setSaveError('');
  };

  const handleSave = async () => {
    const value = text.trim();
    if (!value) {
      setSaveError('Escreva o texto da anotação.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      if (editing) {
        await patientNotesService.update(editing.id, value, visibility, priority);
        showToast('Anotação atualizada.', 'success');
      } else {
        await patientNotesService.create(patientId, value, visibility, priority);
        showToast('Anotação gravada.', 'success');
      }
      setSaving(false);
      setIsModalOpen(false);
      setEditing(null);
      setText('');
      if (page === 1) load(); else setPage(1);
    } catch (err: any) {
      setSaving(false);
      setSaveError(mapPatientNoteError(err));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await patientNotesService.remove(toDelete.id);
      showToast('Anotação excluída.', 'success');
      setToDelete(null);
      load();
    } catch (err: any) {
      showToast(mapPatientNoteError(err), 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Seleção para impressão (RN-534e): sobrevive à troca de página/filtros
  const toggleSelected = (id: string) => {
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };
  const pageIds = items.map(n => n.id);
  const allExpanded = items.length > 0 && items.every(n => expanded.includes(n.id));
  const toggleExpandAll = () => setExpanded(allExpanded ? [] : items.map(n => n.id));
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.includes(id));
  const somePageSelected = pageIds.some(id => selected.includes(id)) && !allPageSelected;
  const toggleSelectPage = () => {
    setSelected(prev => (allPageSelected
      ? prev.filter(id => !pageIds.includes(id))
      : Array.from(new Set([...prev, ...pageIds]))));
  };
  const openPrint = () => {
    window.open(`?page=notes-print&patientId=${patientId}&ids=${selected.join(',')}`, '_blank');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const dirty = (editing ? text.trim() !== editing.note.trim() : text.trim().length > 0) || (editing ? (visibility !== editing.visibility || priority !== editing.priority) : true);

  return (
    <div className="tab-content-section">
      {/* Filtros reativos + nova anotação */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: '21px' }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder={`Digite ao menos ${MIN_SEARCH_LENGTH} letras...`}
          value={search}
          onChange={(e) => changeFilter(setSearch)(e.target.value)}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={searchFieldSx}
        />
        <TextField
          select
          size="small"
          label="Usuário"
          value={createdBy}
          onChange={(e) => changeFilter(setCreatedBy)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {authors.map(a => (
            <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          size="small"
          label="De"
          value={from}
          onChange={(e) => changeFilter(setFrom)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          type="date"
          size="small"
          label="Até"
          value={to}
          onChange={(e) => changeFilter(setTo)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        <Tooltip title={selected.length === 0 ? 'Selecione ao menos uma anotação para imprimir' : `Imprimir ${selected.length} ${selected.length === 1 ? 'anotação selecionada' : 'anotações selecionadas'}`} arrow>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton
              onClick={openPrint}
              disabled={selected.length === 0}
              sx={filterActionButtonSx}
            >
              <Print />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Nova anotação" arrow>
          <span>
            <IconButton
              onClick={openCreate}
              sx={filterActionButtonSx}
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2, px: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          <strong>{totalCount}</strong> {totalCount === 1 ? 'anotação encontrada' : 'anotações encontradas'}
          {selected.length > 0 && <> · <strong>{selected.length}</strong> selecionada{selected.length === 1 ? '' : 's'} para impressão</>}
        </Typography>
        <Tooltip title={allExpanded ? 'Fechar todas' : 'Abrir todas'} arrow>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton size="small" onClick={toggleExpandAll} disabled={items.length === 0} sx={{ color: colors.primary }}>
              {allExpanded ? <UnfoldLess fontSize="small" /> : <UnfoldMore fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : error ? (
        <Typography sx={{ color: colors.error, fontSize: '0.9rem', px: 1, py: 2 }}>{error}</Typography>
      ) : items.length === 0 ? (
        <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem', textAlign: 'center', py: 4 }}>
          Nenhuma anotação encontrada para este paciente.
        </Typography>
      ) : (
        <Box className="c4u-table c4u-table--serial">
          <Box className="c4u-table__header">
            <Box className="admin-plans-header-cell" sx={{ flex: '0 0 44px', justifyContent: 'center' }}>
              <Tooltip title="Selecionar todas desta página para impressão" arrow>
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={toggleSelectPage}
                  disabled={items.length === 0}
                  sx={{ p: 0.25, color: colors.primary, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: colors.primary } }}
                />
              </Tooltip>
            </Box>
            <Box className="admin-plans-header-cell" sx={{ flex: '0 0 130px' }}>Data</Box>
            <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: '1 1 180px', minWidth: 140 }}>Autor</Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '2 1 260px', minWidth: 160 }}>Anotação</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: '0 0 56px', justifyContent: 'center' }}>Ver</Box>
            <Box className="admin-plans-header-cell" sx={{ flex: '0 0 110px', justifyContent: 'flex-end' }}>Ações</Box>
          </Box>

          <Box className="admin-plans-table-body">
            {items.map(note => (
              <React.Fragment key={note.id}>
                <Box className="c4u-table__row">
                  <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 44px', justifyContent: 'center' }} data-label="Imprimir">
                    <Tooltip title="Selecionar para impressão" arrow>
                      <Checkbox
                        size="small"
                        checked={selected.includes(note.id)}
                        onChange={() => toggleSelected(note.id)}
                        sx={{ p: 0.5, color: colors.primary, '&.Mui-checked': { color: colors.primary } }}
                      />
                    </Tooltip>
                  </Box>
                  <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 130px', gap: 0.75 }} data-label="Data">
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatIsoDate(note.createdAt)}</Typography>
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{timeOf(note.createdAt)}</Typography>
                  </Box>
                  <Box className="admin-plans-cell c4u-col--lg" sx={{ display: 'flex', alignItems: 'center', flex: '1 1 180px', minWidth: 140 }} data-label="Autor">
                    <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>{note.createdByName}</Typography>
                  </Box>
                  <Box className="admin-plans-cell c4u-col--md" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '2 1 260px', minWidth: 160 }} data-label="Anotação">
                    {(() => { const p = NOTE_PRIORITIES.find(x => x.value === note.priority) ?? NOTE_PRIORITIES[1]; return (
                      <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: p.color, backgroundColor: p.bg, borderRadius: '999px', px: 1, py: 0.25, flexShrink: 0 }}>{p.label}</Box>
                    ); })()}
                    {note.visibility === 'author' && (
                      <Tooltip title="Somente você vê esta anotação" arrow><Lock sx={{ fontSize: 14, color: colors.textSecondary, flexShrink: 0 }} /></Tooltip>
                    )}
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {note.note.split(/\r?\n/)[0]}
                    </Typography>
                  </Box>
                  <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 56px', justifyContent: 'center' }} data-label="Ver">
                    <Tooltip title={isExpanded(note.id) ? 'Fechar anotação' : 'Ver anotação'} arrow>
                      <IconButton size="small" onClick={() => toggleExpanded(note.id)} sx={{ color: colors.primary }}>
                        {isExpanded(note.id) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box className="admin-plans-cell admin-plans-actions" sx={{ flex: '0 0 110px' }} data-label="Ações">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                      <Tooltip
                        title={note.canEdit
                          ? 'Editar anotação'
                          : note.isMine ? `Prazo de edição encerrado (${editWindowDays} dias)` : 'Somente quem escreveu pode alterar'}
                        arrow
                      >
                        <span>
                          <IconButton className="c4u-row-action" size="small" disabled={!note.canEdit} onClick={() => openEdit(note)} sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryHover } }}>
                            <Edit sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={note.canEdit
                          ? 'Excluir anotação'
                          : note.isMine ? `Prazo de exclusão encerrado (${editWindowDays} dias)` : 'Somente quem escreveu pode excluir'}
                        arrow
                      >
                        <span>
                          <IconButton className="c4u-row-action" size="small" disabled={!note.canEdit} onClick={() => setToDelete(note)} sx={{ backgroundColor: colors.error, '&:hover': { backgroundColor: '#c82333' } }}>
                            <Delete sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                {isExpanded(note.id) && (
                  <Box className="c4u-table__expand" sx={{ px: { xs: 1.5, md: 3 }, py: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e2e8f0', mb: { xs: 1.5, md: 0 } }}>
                    <NoteText text={note.note} />
                    <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted, mt: 1 }}>
                      Registrada por {note.createdByName} em {formatIsoDate(note.createdAt)} {timeOf(note.createdAt)}
                      {note.modifiedAt && note.modifiedAt.slice(0, 16) !== note.createdAt.slice(0, 16) && ` · editada em ${formatIsoDate(note.modifiedAt)} ${timeOf(note.modifiedAt)}`}
                      {!note.canEdit && ' · Somente leitura'}
                    </Typography>
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}

      {totalCount > 0 && !error && (
        <StandardPagination
          currentPage={page}
          totalPages={totalPages}
          itemsPerPage={pageSize}
          totalItems={totalCount}
          onPageChange={setPage}
          onItemsPerPageChange={(n) => { setPageSize(n); setPage(1); }}
        />
      )}

      {/* Modal de inserção/edição (padrão canônico: header teal, overlay de gravação) */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden', position: 'relative' } }}
      >
        <SavingOverlay open={saving} message="Gravando anotação..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="h3" sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {editing ? 'Editar anotação' : 'Nova anotação'}
          </Typography>
          <IconButton onClick={closeModal} sx={{ color: colors.white, padding: '0.25rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5, rowGap: 2, mb: 2 }}>
            <TextField
              select
              size="small"
              label="Quem pode ver"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as NoteVisibility)}
              InputLabelProps={{ shrink: true }}
              helperText={visibility === 'author' ? 'Somente você lê esta anotação.' : 'Toda a equipe com acesso ao cadastro lê.'}
            >
              {NOTE_VISIBILITIES.map((v) => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Importância"
              value={priority}
              onChange={(e) => setPriority(e.target.value as NotePriority)}
              InputLabelProps={{ shrink: true }}
            >
              {NOTE_PRIORITIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
          </Box>
          {saveError && (
            <Box sx={{ backgroundColor: '#fde2e4', color: colors.error, borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.85rem', mb: 2 }}>
              {saveError}
            </Box>
          )}
          <TextField
            label="Anotação"
            multiline
            minRows={5}
            fullWidth
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            inputProps={{ maxLength: PATIENT_NOTE_MAX_LENGTH }}
            helperText={`${text.length}/${PATIENT_NOTE_MAX_LENGTH}`}
            InputLabelProps={{
              shrink: true,
              sx: {
                fontSize: inputs.default.labelFontSize,
                color: inputs.default.labelColor,
                backgroundColor: inputs.default.labelBackground,
                padding: inputs.default.labelPadding,
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': { fontSize: inputs.default.fontSize },
              // Neutraliza o CSS global de textarea (senão o textarea-sombra do MUI vaza)
              '& textarea': { minHeight: '0 !important', border: 'none !important', padding: '0 !important' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
          <Button onClick={closeModal} disabled={saving} sx={{ color: colors.textSecondary, textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !dirty || !text.trim()}
            sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}
          >
            {editing ? 'Salvar' : 'Gravar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!toDelete}
        title="Excluir anotação"
        message="A anotação será excluída definitivamente."
        confirmButtonText="Excluir"
        type="danger"
        isProcessing={deleting}
        onConfirm={handleDelete}
        onCancel={() => { if (!deleting) setToDelete(null); }}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
};

export default PatientNotesTab;
